import React from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Breadcrumbs, Link, Typography, CircularProgress } from '@mui/material';

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Parts from './pages/Parts';
import Services from './pages/Services';
import Products from './pages/Products';
import Quotes from './pages/Quotes';
import QuoteDetail from './pages/QuoteDetail';
import QuoteNew from './pages/QuoteNew';
import WorkOrders from './pages/WorkOrders';
import OrdersCalendar from './pages/OrdersCalendar';
import OrderDetail from './pages/OrderDetail';
import TimeTracking from './pages/TimeTracking';
import TimeTrackingKiosk from './pages/TimeTrackingKiosk';
import TimeEntries from './pages/TimeEntries';
import Timecards from './pages/Timecards';
import Admin from './pages/Admin';
import ProcurementPO from './pages/ProcurementPO';
import ProcurementPODetail from './pages/ProcurementPODetail';
import ProcurementVendors from './pages/ProcurementVendors';
import ProcurementVendorDetail from './pages/ProcurementVendorDetail';
import ProcurementReceiving from './pages/ProcurementReceiving';
import ProcurementReceivingDetail from './pages/ProcurementReceivingDetail';
import ProcurementInvoices from './pages/ProcurementInvoices';
import HumanResources from './pages/HumanResources';
import Inventory from './pages/Inventory';
import Quality from './pages/Quality';
import PackingSlips from './pages/PackingSlips';
import PackingSlipDetail from './pages/PackingSlipDetail';
import CustomerInvoices from './pages/CustomerInvoices';
import CustomerInvoiceDetail from './pages/CustomerInvoiceDetail';
import CustomerPayments from './pages/CustomerPayments';
import PaymentDetail from './pages/PaymentDetail';
import ARAging from './pages/ARAging';
import SalesReport from './pages/SalesReport';
import PayrollReport from './pages/PayrollReport';

// Context
import { AuthProvider, useAuth } from './hooks/useAuth';
import { KIOSK_PILOT_EMAIL } from './constants/kiosk';

// Ledger theme: teal primary + cool backgrounds (matches login “card” treatment app-wide)
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00796b',
      light: '#48a999',
      dark: '#004d40',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#c77800',
      contrastText: '#ffffff',
    },
    background: {
      default: '#eef6f5',
      paper: '#ffffff',
    },
    divider: 'rgba(0, 77, 64, 0.12)',
    action: {
      hover: 'rgba(0, 121, 107, 0.07)',
      selected: 'rgba(0, 121, 107, 0.1)',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  spacing: 6, // Reduce default spacing from 8px to 6px
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          padding: '4px 12px', // Denser button padding
        },
        sizeSmall: {
          padding: '2px 8px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 12px', // Reduced from default 16px
        },
        body: {
          '&:first-of-type': {
            fontWeight: 600,
          },
        },
        head: {
          padding: '8px 12px',
          fontWeight: 600,
        },
        sizeSmall: {
          padding: '4px 8px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: '0 8px 32px rgba(0, 77, 64, 0.12)',
          borderRadius: theme.shape.borderRadius,
          borderTop: `4px solid ${theme.palette.primary.main}`,
          padding: 0,
          border: `1px solid rgba(0, 77, 64, 0.08)`,
          backgroundImage: 'none',
        }),
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: 'rgba(0, 121, 107, 0.05)',
          borderBottom: `1px solid rgba(0, 77, 64, 0.1)`,
          paddingTop: theme.spacing(1.5),
          paddingBottom: theme.spacing(1.5),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px !important',
          '&:last-child': {
            paddingBottom: '16px !important',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: '8px', // Reduced default padding
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 4px 18px rgba(0, 77, 64, 0.08)',
        },
        elevation2: {
          boxShadow: '0 6px 22px rgba(0, 77, 64, 0.1)',
        },
        elevation3: {
          boxShadow: '0 8px 28px rgba(0, 77, 64, 0.11)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '8px', // Reduced default margin
        },
      },
      defaultProps: {
        margin: 'dense', // Use dense margin by default
        size: 'small', // Use small size by default
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
          },
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important', // Reduced from default 64px
          padding: '0 12px !important',
        },
        dense: {
          minHeight: '40px !important',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,77,64,0.18)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#eef6f5',
          backgroundImage:
            'radial-gradient(ellipse 110% 70% at 50% -18%, rgba(0, 121, 107, 0.09), transparent 52%)',
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          marginTop: '0 !important',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '24px', // Reduced from default 32px
          fontSize: '0.75rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '40px', // Reduced from default 48px
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '40px', // Reduced from default 48px
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Root route: if user is kiosk pilot, send them to kiosk when enabled; otherwise full app dashboard
const KioskRedirectOrDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [kioskEnabled, setKioskEnabled] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/public/kiosk-enabled')
      .then((res) => {
        if (!cancelled) setKioskEnabled(res.data?.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setKioskEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (authLoading || kioskEnabled === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isKioskPilot = user?.email && user.email.toLowerCase() === KIOSK_PILOT_EMAIL;
  if (isKioskPilot && kioskEnabled) {
    return <Navigate to="/time-tracking/kiosk" replace />;
  }
  return (
    <MainLayout>
      <Dashboard />
    </MainLayout>
  );
};

// Visiting /logout clears session and redirects to login (no button on kiosk)
const LogoutRedirect = () => {
  const { logout } = useAuth();
  React.useEffect(() => {
    logout();
  }, [logout]);
  return <Navigate to="/login" replace />;
};

const LegacyReceivingDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/procurement/shipments-in/${id}` : '/procurement/shipments-in'} replace />;
};

// Main Layout Component
const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const location = useLocation();

  const breadcrumbItems = React.useMemo(() => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return [];

    const isIdSegment = (value) => /^\d+$/.test(value);
    const titleize = (value) => value.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    const push = (label, to) => items.push({ label, to });
    const items = [];

    if (segments[0] === 'quotes') {
      push('Sales', null);
      push('Quotes', '/quotes');
      if (segments[1] === 'new') push('New Quote', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Quote Detail', null);
      return items;
    }
    if (segments[0] === 'work-orders') {
      push('Sales', null);
      push('Orders', '/work-orders');
      if (segments[1] === 'calendar') push('Calendar', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Order Detail', null);
      return items;
    }
    if (segments[0] === 'customers') {
      push('Sales', null);
      push('Customers', '/customers');
      if (segments[1] && isIdSegment(segments[1])) push('Customer Detail', null);
      return items;
    }
    if (segments[0] === 'customer-invoices') {
      push('Sales', null);
      push('Customer Invoices', '/customer-invoices');
      if (segments[1] && isIdSegment(segments[1])) push('Customer Invoice Detail', null);
      return items;
    }
    if (segments[0] === 'customer-payments') {
      push('Sales', null);
      push('Customer Payments', '/customer-payments');
      if (segments[1] && isIdSegment(segments[1])) push('Payment Detail', null);
      return items;
    }
    if (segments[0] === 'packing-slips') {
      push('Sales', null);
      push('Shipments', '/packing-slips');
      if (segments[1] === 'new') push('New Shipment', null);
      else if (segments[1] && isIdSegment(segments[1])) push('Shipment Detail', null);
      return items;
    }
    if (segments[0] === 'procurement') {
      push('Procurement', null);
      if (segments[1] === 'po') {
        push('Purchase Orders', '/procurement/po');
        if (segments[2] === 'new') push('New Purchase Order', null);
        else if (segments[2] && isIdSegment(segments[2])) push('Purchase Order Detail', null);
      } else if (segments[1] === 'shipments-in') {
        push('Shipments Received', '/procurement/shipments-in');
        if (segments[2] === 'new') push('New Shipment Received', null);
        else if (segments[2] && isIdSegment(segments[2])) push('Shipment Received Detail', null);
      } else if (segments[1] === 'vendors') {
        push('Vendors', '/procurement/vendors');
        if (segments[2] === 'new') push('New Vendor', null);
        else if (segments[2] && isIdSegment(segments[2])) push('Vendor Detail', null);
      } else if (segments[1] === 'invoices') {
        push('Vendor Invoices', '/procurement/invoices');
      } else {
        push(titleize(segments[1] || ''), null);
      }
      return items;
    }

    push(titleize(segments[0]), segments.length > 1 ? `/${segments[0]}` : null);
    for (let i = 1; i < segments.length; i += 1) {
      const seg = segments[i];
      const isLast = i === segments.length - 1;
      let label = isIdSegment(seg) ? 'Detail' : titleize(seg);
      if (seg === 'new') label = `New ${titleize(segments[i - 1] || 'Item')}`;
      push(label, isLast ? null : `/${segments.slice(0, i + 1).join('/')}`);
    }
    return items;
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1.5, // Reduced from p: 3 (24px to 12px)
          marginTop: '48px', // Reduced from 64px to match toolbar height
          // No margin-left needed - sidebar overlays content
          width: '100%',
          position: 'relative',
          zIndex: 1,
          backgroundColor: 'transparent',
          minHeight: 'calc(100vh - 48px)',
        }}
      >
        {breadcrumbItems.length > 0 && (
          <Breadcrumbs separator=">" sx={{ color: 'text.secondary', mb: 1 }}>
            {breadcrumbItems.map((item, idx) => {
              const isLast = idx === breadcrumbItems.length - 1;
              if (item.to && !isLast) {
                return (
                  <Link
                    key={`${item.label}-${idx}`}
                    component={RouterLink}
                    to={item.to}
                    underline="hover"
                    color="inherit"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <Typography key={`${item.label}-${idx}`} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {item.label}
                </Typography>
              );
            })}
          </Breadcrumbs>
        )}
        {children}
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/logout" element={<LogoutRedirect />} />
            <Route path="/kiosk" element={<Navigate to="/time-tracking/kiosk" replace />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <KioskRedirectOrDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Customers />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customers/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/parts"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Parts />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/services"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Services />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Products />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Quotes />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <QuoteNew />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotes/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <QuoteDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <WorkOrders />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/calendar"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OrdersCalendar />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/work-orders/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <OrderDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-invoices"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerInvoices />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-invoices/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerInvoiceDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-payments"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <CustomerPayments />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-payments/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PaymentDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/ar-aging"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ARAging />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sales-summary"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <SalesReport />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/payroll-hours"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PayrollReport />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TimeTracking />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/time-entries"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <TimeEntries />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/timecards"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Timecards />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/time-tracking/kiosk"
              element={<TimeTrackingKiosk />}
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Admin />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/vendors/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementVendorDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/vendors"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementVendors />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/po"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementPO />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/po/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementPODetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/po/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementPODetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/receiving"
              element={<Navigate to="/procurement/shipments-in" replace />}
            />
            <Route
              path="/procurement/receiving/new"
              element={<Navigate to="/procurement/shipments-in/new" replace />}
            />
            <Route
              path="/procurement/receiving/:id"
              element={<LegacyReceivingDetailRedirect />}
            />
            <Route
              path="/procurement/shipments-in"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementReceiving />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/shipments-in/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementReceivingDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/shipments-in/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementReceivingDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/procurement/invoices"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <ProcurementInvoices />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <HumanResources />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Inventory />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packing-slips"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PackingSlips />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packing-slips/new"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PackingSlipDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packing-slips/:id"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PackingSlipDetail />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/quality"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Quality />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
